"use client";

import { Card } from "@hexclave/ui";
import { cn } from "@hexclave/ui";
import React from "react";

// ─── Card nesting context ────────────────────────────────────────────────────
// Components with a `glassmorphic` prop use this to auto-detect whether they
// sit inside a DesignCard.  When they do, glassmorphic defaults to `true`;
// when they don't, it defaults to `false`.

const DesignCardNestingContext = React.createContext(false);

/**
 * Returns `true` when the calling component is rendered inside a DesignCard.
 * Useful for deriving a glassmorphic default.
 */
export function useInsideDesignCard(): boolean {
  return React.useContext(DesignCardNestingContext);
}

/**
 * Resolve the effective glassmorphic value.
 * - If the caller passed an explicit boolean → honour it.
 * - Otherwise → fall back to whether we're inside a DesignCard.
 */
export function useGlassmorphicDefault(explicit: boolean | undefined): boolean {
  const insideCard = useInsideDesignCard();
  return explicit ?? insideCard;
}

type DesignCardGradient = "blue" | "cyan" | "purple" | "green" | "orange" | "default";

const hoverTintClasses = new Map<DesignCardGradient, string>([
  ["blue", "group-hover:bg-blue-500/[0.03]"],
  ["purple", "group-hover:bg-purple-500/[0.03]"],
  ["green", "group-hover:bg-emerald-500/[0.03]"],
  ["orange", "group-hover:bg-orange-500/[0.03]"],
  ["default", "group-hover:bg-slate-500/[0.02]"],
  ["cyan", "group-hover:bg-cyan-500/[0.03]"],
]);

const demoTintClasses = new Map<DesignCardGradient, string>([
  ["blue", "group-hover/tint:bg-blue-500/[0.02]"],
  ["purple", "group-hover/tint:bg-purple-500/[0.02]"],
  ["green", "group-hover/tint:bg-emerald-500/[0.02]"],
  ["orange", "group-hover/tint:bg-orange-500/[0.02]"],
  ["default", "group-hover/tint:bg-slate-500/[0.015]"],
  ["cyan", "group-hover/tint:bg-cyan-500/[0.02]"],
]);

const bodyPaddingClass = "p-5";

// ─── Discriminated props ──────────────────────────────────────────────────
// - If title is given, icon is required.
// - The layout is derived automatically:
//     title + subtitle → "header"   (full header block with subtitle)
//     title only       → "compact"  (slim bar with border-b)
//     no title         → "bodyOnly" (just the body)

type DesignCardBaseProps = {
  glassmorphic?: boolean,
  gradient?: DesignCardGradient,
  contentClassName?: string,
} & Omit<React.ComponentProps<typeof Card>, "title">;

type WithTitleProps = {
  title: React.ReactNode,
  subtitle?: React.ReactNode,
  icon: React.ElementType,
  actions?: React.ReactNode,
};

type WithoutTitleProps = {
  title?: never,
  subtitle?: never,
  icon?: never,
  actions?: never,
};

export type DesignCardProps = DesignCardBaseProps & (WithTitleProps | WithoutTitleProps);

/**
 * General-purpose card for grouping related content: section headers,
 * description blocks, chart-less panels, etc. If the content is a
 * big-number metric, use `DesignMetricCard` instead. If it contains a
 * chart, wrap it in `DesignChartCard`.
 *
 * Two shapes, picked automatically by the props you pass:
 *
 * ```tsx
 * // With a header (title requires icon):
 * <DesignCard title="Recent activity" icon={ActivityIcon}>
 *   ...body...
 * </DesignCard>
 *
 * // Body-only (no header):
 * <DesignCard>
 *   <h2 className="text-2xl font-semibold">Dashboard</h2>
 *   <p className="text-muted-foreground">Overview of your user base</p>
 * </DesignCard>
 * ```
 *
 * Rules:
 * - DO NOT add padding classes (`p-6`, `p-5`, etc.) to `className` — the
 *   component already has built-in padding and extra padding will look wrong.
 * - If you set `title`, you MUST also set `icon`. The TS types enforce this,
 *   but writing `title` without `icon` will fail.
 * - Body-only cards (no `title`) automatically go transparent in dark mode.
 */
export function DesignCard({
  title,
  subtitle,
  icon: Icon,
  actions,
  glassmorphic: glassmorphicProp,
  gradient = "default",
  children,
  className,
  contentClassName,
  ...props
}: DesignCardProps) {
  const glassmorphic = glassmorphicProp ?? true;
  const hoverTintClass = hoverTintClasses.get(gradient) ?? "group-hover:bg-slate-500/[0.02]";
  const hasContent = React.Children.count(children) > 0;

  // Derive layout from which props were provided
  const variant = title != null
    ? (subtitle != null ? "header" : "compact")
    : "bodyOnly";

  return (
    <DesignCardNestingContext.Provider value={true}>
      <Card
        className={cn(
          "group relative rounded-2xl overflow-hidden",
          glassmorphic && [
            "bg-white/90 dark:bg-background/60 dark:backdrop-blur-xl border-0 transition-all duration-150 hover:transition-none",
            "ring-1 ring-black/[0.06] hover:ring-black/[0.1] dark:ring-white/[0.06] dark:hover:ring-white/[0.1]",
            "shadow-none",
          ],
          glassmorphic && variant === "bodyOnly" && "dark:bg-transparent dark:ring-0 dark:shadow-none",
          className
        )}
        {...props}
      >
        {glassmorphic && (
          <>
            <div className={cn(
              "absolute inset-0 bg-gradient-to-br from-foreground/[0.04] dark:from-foreground/[0.02] to-transparent pointer-events-none rounded-2xl",
              variant === "bodyOnly" && "dark:hidden"
            )} />
            {variant !== "bodyOnly" && (
              <div
                className={cn(
                  "absolute inset-0 transition-colors duration-150 group-hover:transition-none pointer-events-none rounded-2xl",
                  hoverTintClass
                )}
              />
            )}
          </>
        )}
        <div className="relative">
          {variant === "header" && (
            <div className={bodyPaddingClass}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {Icon && (
                      <div className="p-1.5 rounded-lg bg-foreground/[0.06] dark:bg-foreground/[0.04]">
                        <Icon className="h-3.5 w-3.5 text-foreground/70 dark:text-muted-foreground" />
                      </div>
                    )}
                    <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      {title}
                    </span>
                  </div>
                  {subtitle && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {subtitle}
                    </p>
                  )}
                </div>
                {actions && (
                  <div className="flex-shrink-0">
                    {actions}
                  </div>
                )}
              </div>
            </div>
          )}
          {variant === "compact" && (
            <div className="p-5 flex items-center justify-between gap-4 border-b border-black/[0.12] dark:border-white/[0.06]">
              <div className="flex items-center gap-2 min-w-0">
                {Icon && (
                  <div className="p-1.5 rounded-lg bg-foreground/[0.04]">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                )}
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  {title}
                </span>
              </div>
              {actions && (
                <div className="flex-shrink-0">
                  {actions}
                </div>
              )}
            </div>
          )}
          {hasContent && (
            <div
              className={cn(
                variant === "header" ? "border-t border-black/[0.12] dark:border-white/[0.06]" : "",
                variant === "compact" ? "px-5 py-4" : "",
                variant === "bodyOnly" || variant === "header" ? bodyPaddingClass : "",
                contentClassName
              )}
            >
              {children}
            </div>
          )}
        </div>
      </Card>
    </DesignCardNestingContext.Provider>
  );
}

export type DesignCardTintProps = {
  gradient: DesignCardGradient,
} & React.ComponentProps<"div">

export function DesignCardTint({
  gradient,
  className,
  children,
  ...props
}: DesignCardTintProps) {
  const tintClass = demoTintClasses.get(gradient) ?? "group-hover/tint:bg-slate-500/[0.015]";

  return (
    <div
      className={cn(
        "group/tint relative rounded-2xl bg-white/90 dark:bg-background/60 dark:backdrop-blur-xl transition-all duration-150 hover:transition-none",
        "ring-1 ring-black/[0.06] hover:ring-black/[0.1] dark:ring-white/[0.06] dark:hover:ring-white/[0.1]",
        "shadow-none overflow-hidden",
        className
      )}
      {...props}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.04] dark:from-foreground/[0.02] to-transparent pointer-events-none rounded-2xl" />
      <div
        className={cn(
          "absolute inset-0 transition-colors duration-150 group-hover/tint:transition-none pointer-events-none rounded-2xl",
          tintClass
        )}
      />
      <div className="relative">
        {children}
      </div>
    </div>
  );
}
